<script lang="ts">
import { ref, onMounted, computed, nextTick } from "vue";
import { Dropdown, DropdownContent } from "v-dropdown";
import menuProfileData from "~/interfaces/menu-profile";
import { useStoreWithKey } from "~/store";
import auth from "~/services/AuthService";
import loggedInUserImage from "@/assets/images/auth/customer-group-1.png";
import type { MenuProfileItem } from "~/interfaces/MenuProfileItem";
import type { DropdownInstance } from "~/interfaces/DropdownInstance";
import eventBus from "~/event-bus";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { RouteRecord } from "vue-router";

export default {
  components: {
    Dropdown,
    DropdownContent,
  },
  setup() {
    const dropdownActive = ref(false);
    const activeUrl = ref("");
    const menuProfile = ref<MenuProfileItem[]>([]);
    const menuVDropdown = ref<DropdownInstance>();
    const store = useStoreWithKey();
    const customer = store.getters["auth/getCustomer"] as { name: string };
    const dataCustomer = computed(() => customer);

    eventBus.emit("data-customer", dataCustomer.value);
    onMounted(() => {
      menuProfile.value = menuProfileData as MenuProfileItem[];
    });

    const closeDropdown = () => {
      if (menuVDropdown.value?.visible) {
        menuVDropdown.value.close();
      }
    };

    const onLogout = async () => {
      try {
        await auth.logout();
      } catch (error) {
        console.error(error);
      }
    };

    const onQuickLogout = async () => {
      closeDropdown();
      await onLogout();
    };

    const change = async (val: boolean) => {
      dropdownActive.value = val;

      if (val) {
        await nextTick();
      }
    };

    const activePath = (to: string, name: string) => {
      localStorage.setItem(
        "activeBreadcrumbOrder",
        JSON.stringify({ to: to, name: name }),
      );
      activeUrl.value = to;
      closeDropdown();
    };

    const firstName = computed(() => {
      return customer.name ? customer.name.split(" ")[0] : "Usuario";
    });

    return {
      dropdownActive,
      activeUrl,
      menuProfile,
      loggedInUserImage,
      menuVDropdown,
      dataCustomer,
      onLogout,
      onQuickLogout,
      change,
      activePath,
      closeDropdown,
      firstName,
    };
  },
};
</script>

<template>
  <div>
    <Dropdown
      ref="menuVDropdown"
      align="right"
      class="mt-2"
      @visible-change="change"
    >
      <template #trigger>
        <button
          type="button"
          class="animate-hover-border py-1 px-0 position-relative d-flex justify-content-center align-items-center h-100 box-search-bar btn-dropdown lh-1"
          :class="{
            'bg-danger text-white': dropdownActive,
            'no-active': !dropdownActive,
          }"
        >
          <div class="d-flex flex-column align-items-center">
            <i class="bi bi-person-circle d-lg-none fs-4"></i>
            <img
              alt="Usuario logeado"
              :src="loggedInUserImage"
              class="img-user-profile d-none d-lg-block"
            />
            <div class="d-flex justify-content-cente mt-1">
              <p class="fs-7 d-none d-lg-block name m-0 p-0">
                Hola <span class="fw-semibold">{{ firstName }}</span>
              </p>
              <p class="d-lg-none name m-0 p-0">
                <span class="fw-semibold">{{ firstName }}</span>
              </p>
            </div>
            <div
              class="title-box w-100 text-nowrap text-uppercase d-none d-lg-block"
              :class="{
                'text-white': dropdownActive,
                'text-danger': !dropdownActive,
              }"
            >
              Mi cuenta <i class="bi bi-caret-down-fill small"></i>
            </div>
          </div>
        </button>
      </template>
      <DropdownContent>
        <ul class="list-unstyled px-3 pt-4 container-dropdown-profile">
          <li
            v-for="(item, index) in menuProfile"
            :key="index"
            class="pointer mb-3"
          >
            <router-link
              :class="{
                'text-blue':
                  typeof item.to === 'string'
                    ? $route.path === item.to
                    : $route.name === (item.to as RouteRecord).name,
              }"
              class="d-flex ps-2 pe-4 align-items-center title link-underline link-underline-opacity-0"
              :to="item.to"
              @click="activePath(item.to, item.name)"
            >
              <i v-if="item.icon" class="icon" :class="item.icon" />
              <p class="ms-3 p-0 m-0 text-nowrap">{{ item.name }}</p>
            </router-link>
          </li>
          <li class="pointer mb-3 d-lg-none">
            <button
              type="button"
              class="d-flex ps-2 pe-4 align-items-center title btn btn-link p-0 text-decoration-none"
              @click="onQuickLogout()"
            >
              <i class="icon bi bi-power" />
              <p class="ms-3 p-0 m-0 text-nowrap">Cerrar sesión</p>
            </button>
          </li>
        </ul>
        <div
          class="pointer d-none d-lg-flex bg-light px-4 on-logout"
          @click="onLogout()"
        >
          <div
            class="content d-flex align-items-center justify-content-center flex-column"
          >
            <i class="bi bi-power fw-semibold icon"></i>
            <p class="text-center mt-1 fw-semibold">Salir</p>
          </div>
        </div>
      </DropdownContent>
    </Dropdown>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/base/mixins" as mix;
@use "@/assets/scss/_variables" as var;

.container-dropdown-profile {
  .title {
    color: var(--bs-dark);
    @include mix.custom-font(
      $normal-font: var.$normal-font-300,
      $size: 17,
      $line-height: 17
    );
    &:hover {
      color: var(--bs-blue);
    }
    @include mix.extra-extra-large-screen {
      @include mix.custom-font(
        $normal-font: var.$normal-font-300,
        $size: 15,
        $line-height: 15
      );
    }
  }

  .icon {
    font-size: 21px;
    @include mix.extra-extra-large-screen {
      font-size: 19px;
    }
  }
}

.on-logout {
  .content {
    color: var(--bs-dark);

    &:hover {
      color: var(--bs-red);
    }
    p {
      @include mix.custom-font(
        $normal-font: var.$normal-font-300,
        $size: 17,
        $line-height: 17
      );

      @include mix.extra-extra-large-screen {
        @include mix.custom-font(
          $normal-font: var.$normal-font-300,
          $size: 15,
          $line-height: 15
        );
      }
    }
  }
}

.btn-dropdown {
  background-color: transparent;
  @media (min-width: 992px) {
    background-color: var(--bs-white);
  }

  .name {
    max-width: 80px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 10px;
    @media (min-width: 480px) {
      width: 90px;
    }
    @media (min-width: 768px) {
      font-size: 12px;
    }
    @media (min-width: 992px) {
      display: block;
      width: 100px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }

  &.no-active {
    color: var(--bs-white);

    @media (min-width: 992px) {
      color: var(--bs-secondary);
    }
  }

  .img-user-profile {
    @include mix.width-and-height(28, 28);
  }
}
</style>

<style>
.v-dropdown-container {
  border-radius: 0;
  border: 0;
  border-top: 4.5px solid var(--bs-gray-500);
  border-bottom: 4.5px solid var(--bs-gray-500);
  justify-content: space-between;
  right: 0;
  left: auto !important;
}
</style>
